import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import { Alert, Button, Grid, Snackbar, Stack } from "@mui/material";
import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  fetchOssStandards,
  saveOssCalendarDay,
  saveOssItem,
  saveOssItemGroup,
  saveOssSettings,
  recalculateOssSchedules,
} from "../../api/ossApi";
import { OssPageHeader } from "../../components/common/OssPageHeader";
import { OssCalendarDayManager } from "../../components/standards/OssCalendarDayManager";
import { OssItemGroupManager } from "../../components/standards/OssItemGroupManager";
import { OssItemManager } from "../../components/standards/OssItemManager";
import { OssSettingsEditor } from "../../components/standards/OssSettingsEditor";
import { useOssAsyncData } from "../../hooks/useOssAsyncData";

const fallback = { settings: [], itemGroups: [], items: [], calendars: [], calendarDays: [] };

export const OssStandards = () => {
  const { group_code, project_code } = useParams();
  const [toast, setToast] = useState("");
  const [recalculating, setRecalculating] = useState(false);
  const { data, loading, error, setError, load } = useOssAsyncData({
    fetcher: () => fetchOssStandards(group_code, project_code),
    fallbackData: fallback,
    errorMessage: "기준정보를 불러오지 못했습니다.",
    deps: [group_code, project_code],
  });

  const runSave = async (fn, message) => {
    setError("");
    try {
      await fn();
      setToast(message);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || "저장 중 오류가 발생했습니다.");
    }
  };

  const runRecalculation = async () => {
    setError("");
    setRecalculating(true);
    try {
      const result = await recalculateOssSchedules(group_code, project_code, { userKey: "order.manager" });
      setToast(`전체 주문 일정 ${result?.recalculatedCount || 0}건을 재계산했습니다.`);
    } catch (e) {
      setError(e.response?.data?.message || "일정 재계산 중 오류가 발생했습니다.");
    } finally {
      setRecalculating(false);
    }
  };

  return (
    <Stack spacing={1.5}>
      <OssPageHeader
        title="기준정보"
        iconName="settingsIcon"
        actions={(
          <Button
            variant="outlined"
            startIcon={<AutorenewRoundedIcon />}
            disabled={loading || recalculating}
            onClick={runRecalculation}
          >
            전체 주문 재계산
          </Button>
        )}
      />
      {error && <Alert severity="warning">{error}</Alert>}
      <Grid container spacing={1.5}>
        <Grid item xs={12} lg={4}>
          <OssSettingsEditor
            settings={data.settings}
            loading={loading}
            onSave={(settings) => runSave(() => saveOssSettings(group_code, project_code, { settings, userKey: "order.manager" }), "운영 기준을 저장했습니다.")}
          />
        </Grid>
        <Grid item xs={12} lg={8}>
          <OssItemGroupManager
            rows={data.itemGroups}
            loading={loading}
            onSave={(payload) => runSave(() => saveOssItemGroup(group_code, project_code, payload), "품목군을 저장했습니다.")}
          />
        </Grid>
        <Grid item xs={12}>
          <OssItemManager
            rows={data.items}
            itemGroups={data.itemGroups}
            loading={loading}
            onSave={(payload) => runSave(() => saveOssItem(group_code, project_code, payload), "품목을 저장했습니다.")}
          />
        </Grid>
        <Grid item xs={12}>
          <OssCalendarDayManager
            calendars={data.calendars}
            rows={data.calendarDays}
            loading={loading}
            onSave={(payload) => runSave(() => saveOssCalendarDay(group_code, project_code, payload), "달력 예외일을 저장했습니다.")}
          />
        </Grid>
      </Grid>
      <Snackbar open={Boolean(toast)} autoHideDuration={2500} onClose={() => setToast("")} message={toast} />
    </Stack>
  );
};
